import { Card, CARD_SECTION_TITLE_CLASS } from '@safetyhub/ui';
import { MANUAL_RESULT_STALE_DAYS, type StoryCounts } from '../../lib/stories';
import {
  formatRatio,
  scopedCountCaption,
  statusLabel,
  STATUS_HINTS,
  violationTallyDisagrees,
  type StoryScope,
} from './storyMonitorView';

/**
 * 모니터 상단 요약 — **세 비율 중 화면이 그릴 수 있는 것만** 싣고 못 그리는 칸은 «비었음을» 보인다.
 *
 * ## 비율 셋 중 이 카드가 소유하는 것은 **건강도 하나**다
 * 설계가 요구한 비율은 셋이다 — 커버리지(`auto`+`manual`) · 자동 커버리지(`auto`만) · 건강도.
 * 앞의 둘은 숙성(`guard` **소스 스캔** 파생)이 분자라 서버가 모르고, run이 투과한
 * `registry_summary`에서만 온다 ⇒ **`RecentRunsPanel`이 소유한다.** 그 분리가 중요한 이유는
 * 「건강도만 보여주면 **아무것도 안 재는 것이 완벽으로 보인다**」이기 때문이고, 그래서 이 카드는
 * 건강도 옆에 **분모 밖 셋**(공허·측정 실패·미판정)을 항상 함께 싣고 커버리지는 **그 카드를 가리킨다**.
 *
 * ⚠️ 여기 「verify 절차 선언」 수를 커버리지로 읽지 말 것 — 한 스토리가 불변식과 `verify`를 둘 다
 * 가질 수 있어 그 수로 `auto`/`manual`을 가를 수 없다(관측으로 숙성을 재파생하면 `story:check`의
 * 파생과 조용히 어긋난다 — 「검사가 지워졌는데 화면은 `auto`라고 말하는」 가장 위험한 낡음).
 *
 * ## 🔴 이 카드의 수는 **전수가 아닐 수 있다** — 그래서 범위를 함께 싣는다
 * 서버가 `counts: buildCounts(filtered)`를 내므로 **필터가 걸리면 이 카드가 부분집합의 집계를
 * 그린다.** 그런데 2026-10-13까지 그 사실이 화면에 **한 글자도 없었다** — 상태 = `PASS`를 고르면
 * 「건강도 100% · 공허 0 · 실패 0 · 판정 없음 0」이 떠서, 이 카드가 막으려고 존재하는 오독
 * (**아무것도 안 재는 것이 완벽으로 보인다**)을 **자기가 만들었다.** 500캡은 `partial` 배너가
 * 덮지만 필터 축은 아무도 덮지 않았다.
 * ⇒ `scope`를 받아 `partial` 배너와 **같은 모양**으로 한 줄 띄우고, 스토리 수 캡션도
 * `scopedCountCaption` **한 함수**에서 수와 라벨을 함께 만든다(따로 쓰면 숫자만 고쳐지고 라벨이
 * 거짓으로 남는데, 그 거짓은 **아무 검사도 깨뜨리지 않는다**).
 */
export interface StorySummaryPanelProps {
  counts: StoryCounts;
  /** 필터 적용 후 **스토리 수**. ⚠️ 위반 수가 아니다 */
  total: number;
  /** 위 두 수가 **무엇에 대한 수인가** — `describeStoryScope`가 활성 필터에서 파생한다 */
  scope: StoryScope;
  /** `verify` 절차가 선언된 스토리 수. ⚠️ 커버리지가 아니다 */
  verifyDeclared: number;
  /** 사람 판정에 기대고 있는데 낡은 스토리 수(경과일 대리 신호) */
  manualStale: number;
}

function StatusCell({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint: string;
  tone: string;
}) {
  return (
    <div className="flex flex-col gap-0.5" title={hint}>
      <span className="text-xs font-semibold text-ink-500">{label}</span>
      <span className={`text-xl font-extrabold tabular-nums ${tone}`}>{value}</span>
    </div>
  );
}

function ViolationCell({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-semibold text-ink-500">{label}</span>
      <span className={`text-lg font-extrabold tabular-nums ${tone}`}>{value}</span>
    </div>
  );
}

export default function StorySummaryPanel({
  counts,
  total,
  scope,
  verifyDeclared,
  manualStale,
}: StorySummaryPanelProps) {
  const { byStatus, health, violations } = counts;
  const disagrees = violationTallyDisagrees(violations);

  return (
    <div className="flex flex-col gap-4">
      {/* 필터 축 — `partial`(500캡) 배너와 같은 모양으로 둔다. 두 사실이 같은 종류이기 때문이다:
          「이 수는 전수가 아니다」. 둘 다 없으면 부분 집계가 전수로 읽힌다. */}
      {scope.notice !== null && (
        <p className="rounded-card-sm bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
          {scope.notice === 'subset' ? '⚠️ 필터 적용 중 — 아래 수는 전수가 아니다' : 'ℹ️ 범위 지정 중'}{' '}
          ({scope.axes.map((axis) => `${axis.axis}: ${axis.value}`).join(' · ')})
          {scope.notice === 'subset' && (
            <>
              . 서버가 <strong>필터된 집합으로</strong> 센 값이라 분포·건강도가 그 부분집합의 것이다 —
              예컨대 상태를 「통과」로 좁히면 <strong>건강도는 언제나 100%</strong>이고 공허·실패·미판정이
              0으로 보인다(그것은 「완벽」이 아니라 <strong>그 셋을 뺀 집합</strong>이다).
            </>
          )}
          {/* ⚠️ 갈래를 `subset`의 반대로 쓰지 않는다 — 은퇴 포함만 걸렸을 때 env 문구가 떠서
              **틀린 안내**가 된다(숫자는 맞으니 아무 검사도 깨지지 않는 종류의 거짓이다). */}
          {scope.notice === 'basis' && (
            <>
              . 스토리 집합은 그대로이고 <strong>판정 기준</strong>만 좁혀졌다 — 다른 env에서만 재진
              스토리가 「판정 없음」으로 떨어진다(수가 아니라 <strong>분포</strong>가 바뀐다).
            </>
          )}
          {scope.notice === 'widen' && (
            <>
              . 범위가 <strong>넓어졌다</strong> — 전수보다 큰 집합이라 아래 수가 평소보다 많다.
            </>
          )}
        </p>
      )}

      <Card>
        <div className="flex items-baseline justify-between gap-3">
          <h2 className={CARD_SECTION_TITLE_CLASS}>판정 분포</h2>
          <span className="text-xs font-semibold text-ink-500">
            {/* ⚠️ 수와 범위 라벨을 **한 함수**에서 받는다 — 따로 쓰면 라벨만 낡는다 */}
            스토리 {scopedCountCaption(total, scope)}{' '}
            <span className="text-ink-400">(이 수는 위반 수가 아니다)</span>
          </span>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-5">
          <StatusCell
            label={statusLabel('PASS')}
            value={byStatus.pass}
            hint={STATUS_HINTS.PASS}
            tone="text-emerald-600"
          />
          <StatusCell
            label={statusLabel('VIOLATION')}
            value={byStatus.violation}
            hint={STATUS_HINTS.VIOLATION}
            tone="text-rose-600"
          />
          <StatusCell
            label={statusLabel('VACUOUS')}
            value={byStatus.vacuous}
            hint={STATUS_HINTS.VACUOUS}
            tone="text-amber-600"
          />
          <StatusCell
            label={statusLabel('ERROR')}
            value={byStatus.error}
            hint={STATUS_HINTS.ERROR}
            tone="text-orange-600"
          />
          <StatusCell
            label={statusLabel(null)}
            value={byStatus.unjudged}
            hint={STATUS_HINTS.null}
            tone="text-ink-600"
          />
        </div>

        <p className="mt-3 border-t border-line pt-3 text-xs leading-relaxed text-ink-500">
          <strong className="text-ink-700">건강도 {formatRatio(health.ratio)}</strong> ={' '}
          {health.pass}/{health.denominator} — 분모는 <strong>통과 + 위반</strong>뿐이다.
          공허 {health.vacuous} · 측정 실패 {health.error} · 판정 없음 {health.unjudged}는
          「재지 못했다」라 <strong>분모 밖</strong>이고, 그 셋을 함께 보지 않으면 위 비율이 거짓으로
          읽힌다(아무도 안 쓰는 기능이 100% 초록으로 보인다).
        </p>
      </Card>

      <Card>
        <h2 className={CARD_SECTION_TITLE_CLASS}>보장 수단</h2>
        <div className="mt-3 flex flex-wrap items-start gap-x-8 gap-y-4">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold text-ink-500">verify 절차 선언</span>
            <span className="text-xl font-extrabold tabular-nums text-ink-900">
              {verifyDeclared}
            </span>
            <span className="text-[11px] text-ink-400">판정 절차가 글로 적혀 있다</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold text-ink-500">
              사람 판정이 낡음 ({MANUAL_RESULT_STALE_DAYS}일+)
            </span>
            <span
              className={`text-xl font-extrabold tabular-nums ${
                manualStale > 0 ? 'text-amber-600' : 'text-ink-900'
              }`}
            >
              {manualStale > 0 ? `⚠️ ${manualStale}` : manualStale}
            </span>
            <span className="text-[11px] text-ink-400">경과일 — 배포 횟수가 아니다</span>
          </div>
        </div>

        {/* Callout 갈래(왼쪽 굵은 테두리 · 라운드 없음) — `no-self-painted-card`는 「중립 면 + 라운드
            + (테두리|그림자)」를 볼 때만 발동하므로 이 자리는 대상이 아니다(disable 불필요). */}
        <div className="mt-3 border-l-4 border-ink-300 bg-surface-soft px-3 py-2 text-xs leading-relaxed text-ink-600">
          <strong className="text-ink-800">여기 두 수는 커버리지가 아니다.</strong> 숙성 분포
          (<code>auto</code>/<code>manual</code>/<code>none</code>)와 커버리지 두 비율은{' '}
          <strong>「보장 수단 · 최근 실행」 카드</strong>가 소유한다 — 그 수는 run이 투과한{' '}
          <code>registry_summary</code>에서 오고 <strong>그 run 시점</strong>의 값이다.
          <code> verifyKind</code>를 센 위 수로 <code>auto</code>/<code>manual</code>을 가를 수 없다
          (한 스토리가 불변식과 <code>verify</code>를 둘 다 가질 수 있다).
          <br />
          ⚠️ <strong>이 화면이 못 재는 것 하나</strong> — 「판정 뒤 <strong>배포 N회</strong>」:
          admin이 아는 커밋 해시는 <strong>이 번들 자신의 것</strong>이고 서버 배포 sha를 내리는
          엔드포인트가 없어, 위 낡음 신호는 <strong>경과일</strong>로 대신한다(라벨이 「배포 뒤
          미재판정」이 아닌 이유다). run의 <code>base_sha</code>가 그 축의 재료이니, 배포 sha를 알게
          되면 그쪽으로 바꾼다.
        </div>
      </Card>

      <Card>
        <div className="flex items-baseline justify-between gap-3">
          <h2 className={CARD_SECTION_TITLE_CLASS}>
            위반 {violations.open > 0 ? '🔴' : ''} {violations.open}
          </h2>
          <span className="text-xs font-semibold text-ink-500">
            확인 처리는 <strong>큐에서만</strong> 뺀다 — 위반 수에는 계속 센다
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <ViolationCell
            label="미해소"
            value={violations.open}
            tone={violations.open > 0 ? 'text-rose-600' : 'text-ink-900'}
          />
          <ViolationCell
            label="🔴 큐 (미확인)"
            value={violations.openUnacknowledged}
            tone={violations.openUnacknowledged > 0 ? 'text-rose-600' : 'text-ink-900'}
          />
          <ViolationCell
            label="확인 처리됨"
            value={violations.openAcknowledged}
            tone="text-amber-600"
          />
          <ViolationCell label="해소됨" value={violations.resolved} tone="text-emerald-600" />
        </div>
        {disagrees && (
          <p className="mt-3 rounded-card-sm bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
            ⚠️ 응답 안의 수가 어긋난다 — 미해소 {violations.open} ≠ 미확인{' '}
            {violations.openUnacknowledged} + 확인 처리 {violations.openAcknowledged}. 항등식이
            깨졌으니 <strong>이 수를 근거로 쓰지 말 것</strong>(서버 집계 쪽을 봐야 한다).
          </p>
        )}
      </Card>
    </div>
  );
}
